import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import { Notice } from "@/components/ui/notice";
import { Spinner } from "@/components/ui/spinner";
import { PageLayout } from "@/components/ui/page-layout";
import { PdfViewer } from "@/components/ui/pdf-viewer";
import { usePageTitle } from "@/hooks/use-page-title";
import { AskAdvocateButton } from "@/features/advocate/components/ask-advocate-button";
import { formatDate } from "@/lib/format-date";
import { documentStatusLabel } from "@/lib/document-status-label";
import i18n from "@/lib/i18n";
import { getById, getDownloadUrl } from "../api/progress-reports-api";
import { ProgressReportAnalysisTab } from "./progress-report-analysis-tab";
import type { ProgressReport } from "../types";

const STATUS_VARIANTS: Record<
  string,
  "neutral" | "warning" | "success" | "error"
> = {
  created: "neutral",
  uploaded: "neutral",
  processing: "warning",
  parsed: "success",
  error: "error",
};

function formatPeriod(
  start: string | null,
  end: string | null
): string {
  if (!start && !end) return i18n.t("progress-reports:list.periodNotSet");
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  return formatDate(start || end);
}

export function ProgressReportViewerPage() {
  const { t } = useTranslation(["progress-reports", "iep-documents"]);
  usePageTitle(t("viewerPage.breadcrumbTitle"));
  const { childId, id, prId } = useParams<{
    childId: string;
    id: string;
    prId: string;
  }>();
  const reportId = Number(prId);
  const childIdNumber = childId && /^\d+$/.test(childId) ? Number(childId) : null;
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"document" | "analysis">("document");

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getById(reportId)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setReport(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("viewerPage.loading")} />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-brand-slate-500">{t("viewerPage.notFound")}</p>
        <Link
          to={`/children/${childId}/ieps/${id}`}
          className="text-brand-teal-500 hover:underline mt-2 inline-block"
        >
          {t("viewerPage.backToIep")}
        </Link>
      </div>
    );
  }

  const title =
    report.fileName ||
    formatPeriod(report.reportingPeriodStart, report.reportingPeriodEnd) ||
    t("viewerPage.idFallback", { id: report.id });

  return (
    <PageLayout
      title={title}
      className="max-w-5xl"
      breadcrumb={[
        { label: t("viewerPage.backToIep"), to: `/children/${childId}/ieps/${id}` },
        { label: t("viewerPage.breadcrumbTitle") },
      ]}
      actions={
        childIdNumber != null ? (
          <AskAdvocateButton
            childId={childIdNumber}
            about={{ kind: "progress_report", id: report.id }}
            label={t("viewerPage.askAbout", {
              period: formatPeriod(report.reportingPeriodStart, report.reportingPeriodEnd),
            })}
            data-testid="progress-report-ask-advocate"
          />
        ) : undefined
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant={STATUS_VARIANTS[report.status] || "neutral"}>
            {documentStatusLabel(report.status)}
          </Badge>
          <span className="text-[13px] text-brand-slate-500">
            {formatPeriod(
              report.reportingPeriodStart,
              report.reportingPeriodEnd
            )}
          </span>
        </div>
        {report.notes && (
          <Markdown
            content={report.notes}
            className="text-sm text-brand-slate-600 bg-brand-slate-50 rounded-card p-3 border border-brand-slate-200"
          />
        )}
      </div>

      {report.status === "error" && report.errorMessage && (
        <Notice variant="error" title={t("viewerPage.processingFailedTitle")}>
          {report.errorMessage}
        </Notice>
      )}

      {report.status === "created" && (
        <Notice variant="info" title={t("viewerPage.noFileTitle")}>
          {t("viewerPage.noFileBody")}
        </Notice>
      )}

      {report.status !== "created" && (
        <>
          <div className="flex border-b border-brand-slate-200">
            <button
              onClick={() => setActiveTab("document")}
              data-testid="pr-tab-document"
              className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                activeTab === "document"
                  ? "text-brand-slate-800 border-b-2 border-brand-teal-500"
                  : "text-brand-slate-500 hover:text-brand-slate-800"
              }`}
            >
              {t("viewerPage.tabDocument")}
            </button>
            <button
              onClick={() => setActiveTab("analysis")}
              data-testid="pr-tab-analysis"
              className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                activeTab === "analysis"
                  ? "text-brand-slate-800 border-b-2 border-brand-teal-500"
                  : "text-brand-slate-500 hover:text-brand-slate-800"
              }`}
            >
              {t("viewerPage.tabAnalysis")}
            </button>
          </div>

          {activeTab === "document" && (
            <PdfViewer
              fileName={report.fileName}
              loadUrl={async () => {
                const res = await getDownloadUrl(report.id);
                return res.success && res.data ? res.data.url : null;
              }}
            />
          )}

          {activeTab === "analysis" && (
            <ProgressReportAnalysisTab progressReportId={report.id} />
          )}
        </>
      )}
    </PageLayout>
  );
}
